import React from 'react';
import { CalendarClock, Check } from 'lucide-react';

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const pretty = (isoOrEmpty) => {
  const d = isoOrEmpty ? new Date(isoOrEmpty + 'T00:00:00') : new Date();
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Just-in-time "as of" chooser for to-date questions.
 * Two controls: the as-of date VALUE (defaults to Today) and, only when the
 * model has allowed alternate date fields, the date COLUMN it is measured on.
 */
export function AsOfPopover({ asOfDate, asOfField, dateFields = [], defaultField = '', fiscalStartMonth = 1, onConfirm, onClose }) {
  const [date, setDate] = React.useState(asOfDate || '');
  const [field, setField] = React.useState(asOfField || defaultField || '');
  const ref = React.useRef(null);
  // Flip the popover below the anchor when there isn't room above (landing view
  // sits high on the page and an upward popover would clip off the top).
  const [placement, setPlacement] = React.useState('up');

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (placement === 'up' && rect.top < 8) setPlacement('down');
  }, [placement]);

  React.useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose && onClose(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const now = new Date();
  const endLastMonth = fmt(new Date(now.getFullYear(), now.getMonth(), 0));
  const qStart = Math.floor(now.getMonth() / 3) * 3;
  const endLastQuarter = fmt(new Date(now.getFullYear(), qStart, 0));
  const endLastYear = fmt(new Date(now.getFullYear(), 0, 0));

  const hasAlternates = (dateFields || []).length > 1;
  const fieldLabel = (dateFields.find(f => f.name === field) || {}).label || field;
  const fiscalMonthName = new Date(2000, (Number(fiscalStartMonth) || 1) - 1, 1).toLocaleDateString(undefined, { month: 'long' });
  const line = `Year-to-date${fieldLabel ? ` on ${fieldLabel}` : ''}, through ${pretty(date)} (fiscal year starts ${fiscalMonthName}).`;

  const quick = (label, val, testid) => (
    <button
      onClick={() => setDate(val)}
      className={'text-[10px] px-2 py-1 rounded-md border transition-colors ' + (date === val ? 'border-primary/60 bg-primary/15 text-foreground' : 'border-border/40 text-muted-foreground hover:border-primary/40')}
      data-testid={testid}
    >{label}</button>
  );

  return (
    <div
      ref={ref}
      className={'absolute left-0 w-72 max-h-[80vh] overflow-y-auto rounded-xl border border-border/50 shadow-2xl p-3 z-50 ' + (placement === 'down' ? 'top-full mt-2' : 'bottom-full mb-2')}
      style={{ background: 'var(--glass-bg, rgba(15,20,40,0.98))', backdropFilter: 'blur(12px)' }}
      data-testid="explore-as-of-popover"
    >
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-foreground">
        <CalendarClock className="w-3.5 h-3.5 text-primary" /> As of which date?
      </div>

      <button
        onClick={() => setDate('')}
        className={'w-full mb-2 rounded-lg py-1.5 text-xs font-medium transition-colors ' + (!date ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-foreground hover:bg-muted/60')}
        data-testid="explore-as-of-today"
      >Today ({pretty('')})</button>

      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        {quick('End of last month', endLastMonth, 'explore-as-of-eolm')}
        {quick('End of last quarter', endLastQuarter, 'explore-as-of-eolq')}
        {quick('End of last year', endLastYear, 'explore-as-of-eoly')}
      </div>

      <label className="block text-[10px] text-muted-foreground mb-2">
        Or pick a date
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-full mt-0.5 text-[11px] rounded-md bg-muted/30 border border-border/50 px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
          data-testid="explore-as-of-datepick"
        />
      </label>

      {hasAlternates && (
        <label className="block text-[10px] text-muted-foreground mb-2" data-testid="explore-as-of-measured-against">
          Measured against
          <select
            value={field}
            onChange={e => setField(e.target.value)}
            className="w-full mt-0.5 text-[11px] rounded-md bg-muted/30 border border-border/50 px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
            data-testid="explore-as-of-field-select"
          >
            {dateFields.map(f => (
              <option key={f.name} value={f.name}>{f.label}{f.name === defaultField ? ' (default)' : ''}</option>
            ))}
          </select>
        </label>
      )}

      <p className="text-[9px] text-muted-foreground/80 leading-snug mb-2" data-testid="explore-as-of-resolved">{line}</p>

      <button
        onClick={() => onConfirm(date, hasAlternates ? field : '')}
        className="w-full rounded-lg py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-1"
        data-testid="explore-as-of-confirm"
      ><Check className="w-3.5 h-3.5" /> Use this</button>
    </div>
  );
}
